import * as RadixTooltip from "@radix-ui/react-tooltip";
import type { ReactNode } from "react";

export type InfoFieldTone =
  | "danger"
  | "default"
  | "info"
  | "muted"
  | "success"
  | "warning";

interface InfoFieldProps {
  label: string;
  value?: ReactNode;
  className?: string;
  fillHeight?: boolean;
  strong?: boolean;
  strongClassName?: string;
  tooltip?: string;
  tone?: InfoFieldTone;
}

const toneClassName: Record<InfoFieldTone, string> = {
  danger: "border-red-200 bg-red-50 text-red-800",
  default: "border-slate-200 bg-white text-slate-900",
  info: "border-blue-200 bg-blue-50 text-blue-800",
  muted: "border-slate-200 bg-slate-50 text-slate-900",
  success: "border-emerald-200 bg-emerald-50 text-emerald-800",
  warning: "border-amber-200 bg-amber-50 text-amber-800",
};

export const InfoField = ({
  label,
  value,
  className = "",
  fillHeight = true,
  strong = false,
  strongClassName = "text-base",
  tooltip,
  tone = "default",
}: InfoFieldProps) => {
  const content = (
    <div
      className={`flex min-w-0 flex-1 flex-col rounded-lg border px-2 py-1.5 ${
        fillHeight ? "h-full" : ""
      } ${toneClassName[tone]} ${className}`}
      tabIndex={tooltip ? 0 : undefined}
    >
      <span className="block text-xs font-semibold uppercase text-slate-500">
        {label}
      </span>
      <span
        className={`mt-0.5 block break-words ${
          strong ? `${strongClassName} font-semibold` : "text-sm font-medium"
        }`}
      >
        {value ?? "-"}
      </span>
    </div>
  );

  if (!tooltip) return content;

  return (
    <RadixTooltip.Provider>
      <RadixTooltip.Root delayDuration={100}>
        <RadixTooltip.Trigger asChild>{content}</RadixTooltip.Trigger>
        <RadixTooltip.Portal>
          <RadixTooltip.Content
            align="center"
            className="z-50 max-w-lg rounded-xs bg-gray-500 px-3 py-2.5 text-white shadow-xs shadow-neutral-500"
            collisionPadding={5}
            sideOffset={4}
          >
            {tooltip}
            <RadixTooltip.Arrow className="fill-gray-500" />
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
};
